import { useEffect, useRef, useState, useCallback } from 'react';
import { useAnimationStore } from '../stores/animationStore';
import { AnimationCategory, LoaderSubType, TextSubType, ButtonSubType } from '../types';
import {
  Particle,
  createParticles,
  updateParticle,
  drawBackgroundParticle,
  CursorPosition,
} from '../utils/particles';
import { SunOutlined, MoonOutlined, BorderOutlined, HighlightOutlined } from '@ant-design/icons';
import { ButtonPreview } from './ButtonPreview';

const backgroundStyles: Record<string, React.CSSProperties> = {
  dark: { background: '#1a1a1a' },
  light: { background: '#f0f0f0' },
  transparent: {
    background: ' repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 50% / 20px 20px',
  },
  gradient: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const renderBackgrounds = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  config: any,
  subType: string,
  particles: Particle[],
  isPlaying: boolean
) => {
  // Note: renderBackgrounds is not used in current implementation
};

// ===== Loaders =====

const renderLoaders = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  config: any,
  subType: LoaderSubType
) => {
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const size = config.size || 40;
  const speed = config.speed || 1;
  const color = Array.isArray(config.color) ? config.color[0] : config.color;
  const time = Date.now() * 0.001 * speed;

  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = config.borderWidth || 3;

  switch (subType) {
    case 'spinner': {
      ctx.beginPath();
      ctx.arc(centerX, centerY, size, time * 2, time * 2 + Math.PI * 1.5);
      ctx.stroke();
      break;
    }
    case 'pulse': {
      const pulseSize = size * (0.5 + Math.sin(time * 4) * 0.5);
      ctx.globalAlpha = 0.5 + Math.sin(time * 4) * 0.5;
      ctx.beginPath();
      ctx.arc(centerX, centerY, pulseSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      break;
    }
    case 'dots': {
      const dotCount = 3;
      const dotSpacing = size * 0.8;
      for (let i = 0; i < dotCount; i++) {
        const delay = i * 0.3;
        const alpha = Math.max(0, Math.sin(time * 3 - delay));
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(centerX - dotSpacing + i * dotSpacing, centerY, size * 0.15, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      break;
    }
    case 'ring': {
      ctx.beginPath();
      ctx.arc(centerX, centerY, size, 0, Math.PI * 2);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(centerX, centerY, size, time * 2, time * 2 + Math.PI);
      ctx.stroke();
      break;
    }
    case 'bounce': {
      const bounceY = centerY + Math.sin(time * 4) * size * 0.5;
      ctx.beginPath();
      ctx.arc(centerX, bounceY, size * 0.3, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'progress': {
      const progress = (Math.sin(time) + 1) / 2;
      ctx.beginPath();
      ctx.arc(centerX, centerY, size, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(centerX, centerY, size * 0.7, 0, Math.PI * 2);
      ctx.strokeStyle = '#333';
      ctx.stroke();
      break;
    }
  }
};

// ===== Button Firefly Animation =====

const renderFirefly = (ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, config: any) => {
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const size = config.size || 60;
  const speed = config.speed || 1;
  const time = Date.now() * 0.001 * speed;

  const btnWidth = size * 3;
  const btnHeight = size;
  const borderRadius = config.borderRadius || 8;
  const borderWidth = config.borderWidth || 2;

  // Firefly settings
  const fireflySize = config.fireflySize || 6;
  const fireflyGlow = config.fireflyGlow || 15;
  const fireflySpeed = config.fireflySpeed || 1;
  const fireflyBlink = config.fireflyBlink ?? 0.8;
  const fireflyTrail = config.fireflyTrail ?? true;
  const fireflyBlur = config.fireflyBlur ?? true;
  const fireflyColor =
    config.fireflyColor ||
    (Array.isArray(config.color) ? config.color[0] : config.color) ||
    '#00ffaa';

  // Calculate button path for outline
  const btnX = centerX - btnWidth / 2;
  const btnY = centerY - btnHeight / 2;

  // Draw button background
  ctx.fillStyle = '#2a2a2a';
  ctx.beginPath();
  ctx.roundRect(btnX, btnY, btnWidth, btnHeight, borderRadius);
  ctx.fill();

  // Draw button border
  ctx.strokeStyle = '#444';
  ctx.lineWidth = borderWidth;
  ctx.stroke();

  // Firefly path - runs along button perimeter
  // Perimeter: top, right, bottom, left
  const perimeter = (btnWidth + btnHeight) * 2;
  const position = (time * fireflySpeed * 50) % perimeter;

  let fireflyX = 0;
  let fireflyY = 0;
  let isOnEdge = false;

  // Calculate position on perimeter
  if (position < btnWidth) {
    // Top edge (left to right)
    fireflyX = btnX + position;
    fireflyY = btnY;
    isOnEdge = true;
  } else if (position < btnWidth + btnHeight) {
    // Right edge (top to bottom)
    fireflyX = btnX + btnWidth;
    fireflyY = btnY + (position - btnWidth);
  } else if (position < btnWidth * 2 + btnHeight) {
    // Bottom edge (right to left)
    fireflyX = btnX + btnWidth - (position - btnWidth - btnHeight);
    fireflyY = btnY + btnHeight;
  } else {
    // Left edge (bottom to top)
    fireflyX = btnX;
    fireflyY = btnY + btnHeight - (position - btnWidth * 2 - btnHeight);
  }

  // Blinking effect - varies opacity
  const blinkAlpha = fireflyBlink > 0 ? 0.5 + Math.sin(time * 10) * fireflyBlink * 0.5 : 1;

  // Trail effect - store previous positions
  const trailPositions: { x: number; y: number; alpha: number }[] = [];
  if (fireflyTrail) {
    const trailCount = 8;
    const trailSpacing = 8;
    for (let i = 0; i < trailCount; i++) {
      const trailTime = time - i * 0.02;
      const trailPos = (trailTime * fireflySpeed * 50) % perimeter;
      let tx = 0,
        ty = 0;

      if (trailPos < btnWidth) {
        tx = btnX + trailPos;
        ty = btnY;
      } else if (trailPos < btnWidth + btnHeight) {
        tx = btnX + btnWidth;
        ty = btnY + (trailPos - btnWidth);
      } else if (trailPos < btnWidth * 2 + btnHeight) {
        tx = btnX + btnWidth - (trailPos - btnWidth - btnHeight);
        ty = btnY + btnHeight;
      } else {
        tx = btnX;
        ty = btnY + btnHeight - (trailPos - btnWidth * 2 - btnHeight);
      }

      trailPositions.push({
        x: tx,
        y: ty,
        alpha: (1 - i / trailCount) * 0.6,
      });
    }
  }

  // Draw trail
  if (fireflyTrail && trailPositions.length > 0) {
    trailPositions.forEach((pos, i) => {
      const trailAlpha = pos.alpha * blinkAlpha;
      ctx.save();
      ctx.globalAlpha = trailAlpha;
      ctx.fillStyle = fireflyColor;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, fireflySize * (1 - i * 0.1), 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    });
  }

  // Glow effect around firefly
  if (fireflyGlow > 0) {
    ctx.save();
    ctx.globalAlpha = blinkAlpha * 0.5;

    // Outer glow
    const gradient = ctx.createRadialGradient(
      fireflyX,
      fireflyY,
      0,
      fireflyX,
      fireflyY,
      fireflyGlow * 2
    );
    gradient.addColorStop(0, fireflyColor);
    gradient.addColorStop(0.3, fireflyColor + '80');
    gradient.addColorStop(1, 'transparent');

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(fireflyX, fireflyY, fireflyGlow * 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Inner glow with blur
    if (fireflyBlur) {
      ctx.save();
      ctx.globalAlpha = blinkAlpha * 0.8;
      ctx.shadowColor = fireflyColor;
      ctx.shadowBlur = fireflyGlow;
      ctx.fillStyle = fireflyColor;
      ctx.beginPath();
      ctx.arc(fireflyX, fireflyY, fireflySize, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  // Main firefly dot
  ctx.save();
  ctx.globalAlpha = blinkAlpha;
  ctx.fillStyle = fireflyColor;
  ctx.beginPath();
  ctx.arc(fireflyX, fireflyY, fireflySize, 0, Math.PI * 2);
  ctx.fill();

  // Bright core
  ctx.globalAlpha = blinkAlpha * 0.9;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(fireflyX, fireflyY, fireflySize * 0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Label
  ctx.fillStyle = '#fff';
  ctx.font = '14px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Firefly', centerX, centerY + size);
};

// ===== Text Animations =====

const renderText = (
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  config: any,
  subType: TextSubType
) => {
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  const speed = config.speed || 1;
  const time = Date.now() * 0.001 * speed;
  const color = Array.isArray(config.color) ? config.color[0] : config.color;
  const sampleText = 'Sample Text';

  ctx.fillStyle = color;
  ctx.font = `${config.size ? config.size * 2 : 32}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  switch (subType) {
    case 'typewriter': {
      const charCount = Math.floor((time % 2) * sampleText.length);
      const text = sampleText.substring(0, charCount);
      ctx.fillText(text, centerX, centerY);
      break;
    }
    case 'fade': {
      const alpha = (Math.sin(time * 2) + 1) / 2;
      ctx.globalAlpha = alpha;
      ctx.fillText(sampleText, centerX, centerY);
      ctx.globalAlpha = 1;
      break;
    }
    case 'wave': {
      const text = sampleText;
      const charWidth = ctx.measureText('M').width;
      for (let i = 0; i < text.length; i++) {
        const offsetY = Math.sin(time * 3 + i * 0.5) * 10;
        ctx.fillText(
          text[i],
          centerX - (text.length * charWidth) / 2 + i * charWidth,
          centerY + offsetY
        );
      }
      break;
    }
    case 'gradient': {
      const gradient = ctx.createLinearGradient(centerX - 100, centerY, centerX + 100, centerY);
      const offset = (Math.sin(time) + 1) / 2;
      gradient.addColorStop(0, color);
      gradient.addColorStop(offset, '#fff');
      gradient.addColorStop(1, color);
      ctx.fillStyle = gradient;
      ctx.fillText(sampleText, centerX, centerY);
      break;
    }
    case 'glitch': {
      const glitchOffset = Math.random() > 0.9 ? (Math.random() - 0.5) * 10 : 0;
      ctx.fillStyle = Math.random() > 0.9 ? '#ff0000' : color;
      ctx.fillText(sampleText, centerX + glitchOffset, centerY);
      ctx.fillStyle = Math.random() > 0.9 ? '#00ffff' : color;
      ctx.fillText(sampleText, centerX - glitchOffset, centerY);
      ctx.fillStyle = color;
      ctx.fillText(sampleText, centerX, centerY);
      break;
    }
    case 'typing': {
      const progress = (time % 3) / 3;
      const visibleCount = Math.floor(progress * sampleText.length);
      const text = sampleText.substring(0, visibleCount);
      const cursor = visibleCount < sampleText.length ? '|' : '';
      ctx.fillText(text + cursor, centerX, centerY);
      break;
    }
  }
};

// ===== Button Component with Border Glow Animation =====

const BorderGlowPreview = ({ config }: { config: any }) => {
  const colors = Array.isArray(config.color) ? config.color : [config.color];
  const glowColor = colors[0] || '#ff0000';
  const borderRadius = config.borderRadius || 10;
  const intensity = config.intensity || 0.5;
  const speed = config.speed || 1;

  const duration = 20 / speed;
  const blur = intensity * 8;

  const btnStyle: React.CSSProperties = {
    position: 'relative',
    display: 'inline-block',
    padding: '16px 32px',
    background: '#111',
    color: '#fff',
    border: 'none',
    borderRadius: `${borderRadius}px`,
    cursor: 'pointer',
    fontSize: 16,
    fontWeight: 500,
    outline: 'none',
    zIndex: 1,
  };

  const glowStyle: React.CSSProperties = {
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    background: `linear-gradient(45deg, ${glowColor}, #ff7300, #fffb00, #48ff00, #00ffd5, #002bff, #7a00ff, #ff00c8, ${glowColor})`,
    backgroundSize: '400% 100%',
    borderRadius: `${borderRadius + 2}px`,
    filter: `blur(${blur}px)`,
    opacity: 1,
    zIndex: 0,
    animation: `glowingBorderGlow ${duration}s linear infinite`,
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Animated glow layer */}
      <div style={glowStyle} />

      {/* Button */}
      <button style={btnStyle}>Hover Me</button>

      {/* Keyframes for animation */}
      <style>{`
        @keyframes glowingBorderGlow {
          0% { background-position: 0 0; }
          50% { background-position: 400% 0; }
          100% { background-position: 0 0; }
        }
      `}</style>
    </div>
  );
};

// ===== Main Component =====

export const CanvasPreview = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { currentPreset, isPlaying, previewBackground, setPreviewBackground } = useAnimationStore();
  const animationRef = useRef<number>();
  const particlesRef = useRef<Particle[]>([]);
  const [fps, setFps] = useState(0);
  const fpsRef = useRef({ frames: 0, lastTime: performance.now() });
  const cursorPosRef = useRef<CursorPosition | null>(null);

  const initParticles = (
    canvas: HTMLCanvasElement,
    config: any,
    subType: string,
    scaleToSize?: { width: number; height: number }
  ) => {
    particlesRef.current = createParticles(canvas, config, subType, scaleToSize);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !currentPreset) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      if (currentPreset.category === 'backgrounds') {
        initParticles(canvas, currentPreset.config, currentPreset.subType);
      }
    };
    resize();
    window.addEventListener('resize', resize);

    // Cursor tracking
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      cursorPosRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };
    const handleMouseLeave = () => {
      cursorPosRef.current = null;
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    const config = currentPreset.config;
    const category = currentPreset.category;
    const subType = currentPreset.subType;

    const animate = () => {
      // FPS calculation
      const now = performance.now();
      fpsRef.current.frames++;
      if (now - fpsRef.current.lastTime >= 1000) {
        setFps(fpsRef.current.frames);
        fpsRef.current.frames = 0;
        fpsRef.current.lastTime = now;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = config.opacity;

      switch (category) {
        case 'backgrounds':
          particlesRef.current.forEach((p) => {
            updateParticle(p, canvas, config, subType, cursorPosRef.current);
            drawBackgroundParticle(ctx, p, subType, config, canvas);
          });
          break;
        case 'loaders':
          renderLoaders(ctx, canvas, config, subType as LoaderSubType);
          break;
        case 'text':
          renderText(ctx, canvas, config, subType as TextSubType);
          break;
        case 'buttons':
          break;
        case 'cards':
          ctx.fillStyle = '#666';
          ctx.font = '16px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText(`Cards: ${subType}`, canvas.width / 2, canvas.height / 2);
          break;
      }

      if (category === 'backgrounds' && isPlaying) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    if (category === 'backgrounds' && isPlaying) {
      animate();
    }

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [currentPreset, isPlaying, currentPreset?.config]);

  const bgOptions: { value: typeof previewBackground; icon: React.ReactNode; title: string }[] = [
    { value: 'dark', icon: <MoonOutlined />, title: 'Тёмный' },
    { value: 'light', icon: <SunOutlined />, title: 'Светлый' },
    { value: 'transparent', icon: <BorderOutlined />, title: 'Прозрачный' },
    { value: 'gradient', icon: <HighlightOutlined />, title: 'Градиент' },
  ];

  return (
    <div
      style={{
        flex: 1,
        ...backgroundStyles[previewBackground],
        borderRadius: 12,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* FPS Counter */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          padding: '4px 8px',
          background: 'rgba(0,0,0,0.6)',
          borderRadius: 4,
          fontSize: 11,
          color: fps >= 50 ? '#4ade80' : fps >= 30 ? '#facc15' : '#ef4444',
          fontFamily: 'monospace',
          zIndex: 10,
        }}
      >
        {fps} FPS
      </div>

      {/* Background Switcher */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 4,
          zIndex: 10,
        }}
      >
        {bgOptions.map((bg) => (
          <button
            key={bg.value}
            onClick={() => setPreviewBackground(bg.value)}
            title={bg.title}
            style={{
              width: 28,
              height: 28,
              background: previewBackground === bg.value ? '#3498db' : 'rgba(0,0,0,0.5)',
              border: '1px solid #444',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 14,
              color: '#fff',
            }}
          >
            {bg.icon}
          </button>
        ))}
      </div>

      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          opacity: currentPreset?.category === 'buttons' ? 0 : 1,
        }}
      />

      {/* Render ButtonPreview for buttons category */}
      {currentPreset?.category === 'buttons' && currentPreset?.subType === 'firefly' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <ButtonPreview config={currentPreset.config} subType={currentPreset.subType} />
        </div>
      )}

      {/* Render BorderGlowPreview for border-glow buttons */}
      {currentPreset?.category === 'buttons' && currentPreset?.subType === 'border-glow' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <BorderGlowPreview config={currentPreset.config} />
        </div>
      )}

      {!currentPreset && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: '#666',
            fontSize: 16,
          }}
        >
          Выберите или создайте пресет
        </div>
      )}
    </div>
  );
};
